<template>
  <div
    class="navbar-container d-flex content align-items-center bg-custom-primary rounded"
    :class="{ 'navbar-container--with-account': hasAccountRow }"
  >
    <!-- Nav Menu Toggler: se oculta mientras el panel esta abierto -->
    <ul
      v-if="!isVerticalMenuActive"
      class="nav navbar-nav d-xl-none"
    >
      <li class="nav-item">
        <b-link
          class="nav-link"
          @click="toggleVerticalMenuActive"
        >
          <feather-icon
            icon="MenuIcon"
            size="21"
          />
        </b-link>
      </li>
    </ul>

    <!-- Navegacion atras/adelante: a la izquierda, lejos de la campana -->
    <div class="navbar-history d-flex align-items-center mr-1">
      <b-button
        size="sm"
        class="mr-50"
        :class="{ 'custom-padding': isSmallScreen }"
        variant="outline-light"
        @click="$router.go(-1)"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="15"
        />
      </b-button>
      <b-button
        size="sm"
        :class="{ 'custom-padding': isSmallScreen }"
        variant="outline-light"
        @click="$router.go(1)"
      >
        <feather-icon
          icon="ArrowRightIcon"
          size="15"
        />
      </b-button>
    </div>

    <!-- Left Col -->
    <div
      class="bookmark-wrapper align-items-center flex-grow-1"
      :style="currentBreakPoint === 'xs' ? 'max-width:130px' : ''"
      :class="{ 'text-truncate': truncateText }"
    >
      <navbar-breadcrumb />
    </div>

    <div class="navbar-account">
      <quote-log-launcher @visibility-change="quoteLogVisible = $event" />
      <wallet-balance-chip @visibility-change="walletVisible = $event" />
      <!-- Plan del cliente: visible en cualquier pantalla y atajo a contratar. -->
      <b-badge
        v-if="membershipPlanName"
        :variant="membershipIsPaid ? 'light-success' : 'light-secondary'"
        class="mr-1 membership-pill cursor-pointer"
        @click="$router.push({ name: 'membership' })"
      >
        {{ membershipPlanName }}
      </b-badge>
    </div>

    <b-navbar-nav
      class="nav justify-content-between align-items-center ml-auto"
    >
      <!-- NAVBAR CONTENIDO A LA DERECHA -->
      <div class="d-flex align-items-center ml-auto">

        <notification-bell class="mr-1" />

        <!-- Dropdown de usuario -->
        <b-nav-item-dropdown
          v-if="currentUser.name"
          right
          toggle-class="d-flex align-items-center dropdown-user-link"
          class="dropdown-user"
        >
          <template #button-content>
            <div class="d-lg-flex d-none user-nav text-right mr-2">
              <p
                v-if="displayCompanyName"
                class="user-company mb-0"
              >
                {{ displayCompanyName }}
              </p>
              <p class="user-name font-weight-bolder mb-0">
                {{ currentUser.name }}
              </p>
              <span class="user-status">
                {{ currentUser.role_name | roleName }}
              </span>
            </div>
            <b-avatar
              size="40"
              variant="light-primary"
              badge
              :src="`${currentUser.logo}`"
              class="badge-minimal"
              badge-variant="success"
            />
          </template>

          <b-dropdown-text class="d-lg-none user-dropdown-header">
            <span class="user-dropdown-header__name">{{ currentUser.name }}</span>
            <span
              v-if="displayCompanyName"
              class="user-dropdown-header__detail"
            >{{ displayCompanyName }}</span>
            <span class="user-dropdown-header__detail">{{ currentUser.role_name | roleName }}</span>
          </b-dropdown-text>

          <b-dropdown-divider class="d-lg-none" />

          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="
              $router.push({
                name: 'editCustomer',
                params: { customerId: currentUser.id }
              })
            "
          >
            <feather-icon
              size="16"
              icon="UserIcon"
              class="mr-50"
            />
            <span>{{ $t('profile') }}</span>
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="logout()"
          >
            <feather-icon
              size="16"
              icon="LogOutIcon"
              class="mr-50"
            />
            <span>{{ $t('logout') }}</span>
          </b-dropdown-item>
        </b-nav-item-dropdown>

      </div>
    </b-navbar-nav>
  </div>
</template>

<script>
import {
  BLink,
  BBadge,
  BButton,
  BNavbarNav,
  BNavItemDropdown,
  BDropdownItem,
  BDropdownText,
  BDropdownDivider,
  BAvatar,
} from 'bootstrap-vue'
import { mapGetters, mapMutations } from 'vuex'
// import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
// import projectSelector from '@/components/projects/ProjectSelector.vue'
import NotificationBell from '@comp/notifications/NotificationBell.vue'
import WalletBalanceChip from '@/layouts/components/WalletBalanceChip.vue'
import QuoteLogLauncher from '@/layouts/components/QuoteLogLauncher.vue'
import NavbarBreadcrumb from '@/layouts/components/NavbarBreadcrumb.vue'
import useJwt from '@/auth/jwt/useJwt'

const ACCOUNT_ROW_BODY_CLASS = 'navbar-account-row'

export default {
  components: {
    BLink,
    BBadge,
    BButton,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownText,
    BDropdownDivider,
    BAvatar,
    NotificationBell,
    WalletBalanceChip,
    QuoteLogLauncher,
    NavbarBreadcrumb,
    // projectSelector,

    // Navbar Components
    // DarkToggler,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
    isVerticalMenuActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      truncateText: true,
      walletVisible: false,
      quoteLogVisible: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('app', ['isSmallScreen', 'currentBreakPoint']),
    ...mapGetters('projects', ['projects', 'project']),
    ...mapGetters('memberships', ['currentMembership']),

    // Solo el cliente tiene plan; al equipo y a los proveedores no se les ensena nada.
    membershipPlanName() {
      if (this.currentUser?.role_name !== 'customer') return null

      return this.currentMembership?.plan_name || null
    },

    hasAccountRow() {
      return Boolean(this.membershipPlanName) || this.walletVisible || this.quoteLogVisible
    },

    membershipIsPaid() {
      return this.currentMembership?.plan_key !== 'free'
    },

    displayCompanyName() {
      const directCompany = String(this.currentUser?.company_name || '').trim()
      if (directCompany) return directCompany

      const customerCompany = String(this.currentUser?.customer?.company_name || '').trim()
      if (customerCompany) return customerCompany

      return ''
    },

    validRoute() {
      if (this.currentUser.role_name === 'admin') return false
      if (this.$route.name === 'quotes') return true
      if (this.$route.name === 'groupedExpenses') return true
      if (this.$route.name === 'expenses') return true
      if (this.$route.name === 'incomes') return true
      if (this.$route.name === 'incomesVsExpenses') return true
      if (this.$route.name === 'expensesReport') return true
      if (this.$route.name === 'reports') return true
      if (this.$route.name === 'allExpenses') return true
      if (this.$route.name === 'budget') return true
      return false
    },
  },
  watch: {
    hasAccountRow: {
      immediate: true,
      handler(value) {
        document.body.classList.toggle(ACCOUNT_ROW_BODY_CLASS, value)
      },
    },
  },
  beforeDestroy() {
    document.body.classList.remove(ACCOUNT_ROW_BODY_CLASS)
  },
  methods: {
    ...mapMutations('auth', ['clearCurrentUser']),
    logout() {
      document.documentElement.style.setProperty('--primary-custom', '#340668')
      document.documentElement.style.setProperty(
        '--secondary-custom',
        '#340668',
      )
      // Remove userData from localStorage
      // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
      this.clearCurrentUser()
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

      // Remove userData from localStorage
      localStorage.removeItem('userData')

      // Redirect to login page
      this.$router.push({ name: 'auth-login' })

      // Reset ability
      // this.$ability.update(initialAbility)
    },
  },
}
</script>

<style lang="scss" scoped>
$account-row-gap: 0.65rem;
$account-row-height: 2.3rem;

.custom-padding {
  padding: 4px 4px !important;
}

.user-company {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.1;
}

.user-company,
.user-name {
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navbar-account {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.user-dropdown-header {
  ::v-deep .b-dropdown-text {
    display: flex;
    flex-direction: column;
    min-width: 13rem;
    max-width: 18rem;
    padding: 0.35rem 1.28rem 0.5rem;
  }
}

.user-dropdown-header__name {
  font-weight: 700;
  color: #25233a;
}

.user-dropdown-header__name,
.user-dropdown-header__detail {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-header__detail {
  font-size: 0.8rem;
  color: #6e6b7b;
}

@media (max-width: 767.98px) {
  .navbar-history,
  .dropdown-notification {
    margin-right: 0.5rem !important;
  }

  .dropdown-user ::v-deep .nav-link {
    padding-right: 0;
  }

  .navbar-container--with-account {
    flex-wrap: wrap;

    .bookmark-wrapper {
      flex: 1 1 0;
      min-width: 0;
    }

    .navbar-account {
      order: 1;
      flex: 0 0 100%;
      justify-content: space-between;
      height: calc(#{$account-row-height} + #{$account-row-gap} + 1px);
      margin-top: $account-row-gap;
      padding-top: $account-row-gap;
      border-top: 1px solid rgba(255, 255, 255, 0.16);

      ::v-deep .wallet-chip,
      ::v-deep .quote-log-launcher {
        height: $account-row-height;
        margin-right: 0;
      }

      .membership-pill {
        display: inline-flex;
        align-items: center;
        height: $account-row-height;
        margin-right: 0 !important;
        margin-left: auto;
        padding: 0 0.9rem;
        border: 1px solid rgba(255, 255, 255, 0.22);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.12) !important;
        color: #ffffff !important;
        font-size: 0.8rem;
        letter-spacing: 0.04em;
      }

      .membership-pill.badge-light-success {
        border-color: rgba(40, 199, 111, 0.6);
        background: rgba(40, 199, 111, 0.22) !important;
      }
    }
  }
}
</style>

<style lang="scss">
@media (max-width: 767.98px) {
  body.navbar-account-row .navbar-floating .app-content::before {
    content: '';
    display: block;
    height: calc(2.3rem + 1.3rem + 1px);
  }
}
</style>
